// Service-worker registration, kept apart from the push logic so the push module
// can be reasoned about (and tested) without a worker in the room.
//
// Registration is a convenience, not a gate: the game runs from the tab whether
// or not a worker took, and every failure here is swallowed. The one thing that
// matters is that it never throws into boot — a player whose browser refuses
// service workers (private mode on some, `file://`, an old WebView) loses push,
// not the farm.

/** Root scope, so the worker can claim the whole app and receive deep links. */
export const SERVICE_WORKER_PATH = '/sw.js';

export type ServiceWorkerResult = 'registered' | 'unsupported' | 'unavailable' | 'existing';

interface ServiceWorkerContainer {
  register(path: string, options?: { scope?: string }): Promise<ServiceWorkerRegistration>;
  getRegistration?(scope?: string): Promise<ServiceWorkerRegistration | undefined>;
}

function container(): ServiceWorkerContainer | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as Navigator & { serviceWorker?: ServiceWorkerContainer };
  return nav.serviceWorker ?? null;
}

/**
 * Registers the worker once per page. Repeat calls are free: an already-present
 * registration is reported, not re-registered, so a settings screen can call this
 * on every visit without piling up registrations.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerResult> {
  const sw = container();
  if (!sw) return 'unsupported';
  if (typeof location === 'undefined' || !location.protocol.startsWith('http')) return 'unsupported';
  try {
    const existing = await sw.getRegistration?.(SERVICE_WORKER_PATH);
    if (existing) return 'existing';
    const registration = await sw.register(SERVICE_WORKER_PATH, { scope: '/' });
    return registration ? 'registered' : 'unavailable';
  } catch {
    // Blocked by policy, a bad MIME type, no storage access: never fatal.
    return 'unavailable';
  }
}

/** The active worker for the app, when there is one. */
export async function activeServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  const sw = container();
  if (!sw) return null;
  try {
    const registration = await sw.getRegistration?.(SERVICE_WORKER_PATH);
    return registration ?? null;
  } catch {
    return null;
  }
}
